import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Stack,
  Pagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete from '@mui/material/Autocomplete';
import { createExpenseCategory, createExpense, fetchAllExpenseCategories, fetchAllExpenses } from '../api';

const ExpensePage = () => {
  const [categories, setCategories] = useState([]);
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(true);

  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newExpense, setNewExpense] = useState({
    category: '',
    amount: '',
    description: ''
  });

  const [searchExpenseTerm, setSearchExpenseTerm] = useState("");
  const [allExpenses, setAllExpenses] = useState([]);
  const [expensePage, setExpensePage] = useState(1);
  const EXPENSES_PER_PAGE = 10;

  useEffect(() => {
    fetchCategories();
    fetchExpensesList();
  }, []);

  const fetchCategories = async () => {
    try {
      const all = await fetchAllExpenseCategories();
      setCategories(all);
    } catch (error) {
      setMessage('Error fetching categories.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchExpensesList = async () => {
    setLoading(true);
    try {
      const all = await fetchAllExpenses();
      setAllExpenses(all);
    } catch (error) {
      setMessage('Error fetching expenses.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setLoading(false);
  };

  // Filter expenses based on search term
  const filteredExpenses = allExpenses.filter((expense) => {
    return (
      (expense.category_name && expense.category_name.toLowerCase().includes(searchExpenseTerm.toLowerCase())) ||
      (expense.description && expense.description.toLowerCase().includes(searchExpenseTerm.toLowerCase()))
    );
  });

  // Pagination logic (frontend)
  const totalExpensePages = Math.ceil(filteredExpenses.length / EXPENSES_PER_PAGE);
  const paginatedExpenses = filteredExpenses.slice(
    (expensePage - 1) * EXPENSES_PER_PAGE,
    expensePage * EXPENSES_PER_PAGE
  );

  const handleExpensePageChange = (event, value) => {
    setExpensePage(value);
  };

  const handleCategoryInput = (e) => {
    setNewCategory({ ...newCategory, [e.target.name]: e.target.value });
  };

  const handleExpenseInput = (e) => {
    setNewExpense({ ...newExpense, [e.target.name]: e.target.value });
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await createExpenseCategory(newCategory);
      setMessage('Category added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setNewCategory({ name: '' });
      fetchCategories();
    } catch (error) {
      setMessage('Failed to add category.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await createExpense(newExpense);
      setMessage('Expense added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setNewExpense({ category: '', amount: '', description: '' });
      fetchExpensesList();
    } catch (error) {
      setMessage('Failed to add expense.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  if (loading) {
    return <Box p={4}><Typography>Loading...</Typography></Box>;
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <PageHeader title="Expenses" subtitle="Track and manage your business expenses." />

        {/* Stack replaced with vertical Box for Add Category and Add Expense */}
        <Box display="flex" flexDirection="column" gap={3} mb={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Add Expense Category</Typography>
              <Box
                component="form"
                onSubmit={handleAddCategory}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <TextField
                  label="Category Name"
                  name="name"
                  value={newCategory.name}
                  onChange={handleCategoryInput}
                  required
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Button type="submit" variant="contained" startIcon={<AddIcon />} sx={{ minWidth: 120, height: 40 }}>
                  Add Category
                </Button>
              </Box>
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>Categories:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {categories.map((cat) => (
                    <Paper key={cat.id} sx={{ px: 2, py: 0.5, mb: 1, mr: 1, display: 'inline-block', minWidth: 80, textAlign: 'center' }}>{cat.name}</Paper>
                  ))}
                </Stack>
              </Box>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Add Expense</Typography>
              <Box
                component="form"
                onSubmit={handleAddExpense}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr auto' },
                  gap: 2,
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.name || ''}
                  value={categories.find((cat) => cat.id === newExpense.category) || null}
                  onChange={(_, value) => setNewExpense({ ...newExpense, category: value ? value.id : '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" required size="small" />
                  )}
                  sx={{ minWidth: 160, width: '100%' }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
                <TextField
                  type="number"
                  name="amount"
                  label="Amount"
                  value={newExpense.amount}
                  onChange={handleExpenseInput}
                  required
                  size="small"
                  sx={{ width: '100%' }}
                />
                <TextField
                  name="description"
                  label="Description"
                  value={newExpense.description}
                  onChange={handleExpenseInput}
                  size="small"
                  sx={{ width: '100%' }}
                />
                <Button type="submit" variant="contained" startIcon={<AddIcon />} sx={{ minWidth: 120, height: 40 }}>
                  Add Expense
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>Expense List</Typography>
            <Box mb={3} display="flex" justifyContent="center">
              <TextField
                label="Search expenses by category or description..."
                value={searchExpenseTerm}
                onChange={(e) => setSearchExpenseTerm(e.target.value)}
                variant="outlined"
                sx={{ width: '60%', maxWidth: 400 }}
              />
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedExpenses.map((expense) => (
                    <TableRow key={expense.id} hover>
                      <TableCell>{expense.category_name}</TableCell>
                      <TableCell>৳{expense.amount}</TableCell>
                      <TableCell>{expense.description}</TableCell>
                      <TableCell>{expense.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalExpensePages}
                page={expensePage}
                onChange={handleExpensePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </CardContent>
        </Card>
        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default ExpensePage;
